import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceReinsuranceTreatyListItem } from './insurance-api.service';
import {
  REINSURANCE_TREATY_STATUS_OPTIONS,
  REINSURANCE_TREATY_TYPE_OPTIONS,
  treatyStatusLabelKey,
  treatyTypeLabelKey
} from './insurance-reinsurance-schemas';

@Component({
  standalone: true,
  selector: 'ain-insurance-reinsurance-treaties-shell',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.reinsurance.treaties.title')"
      [description]="t('insurance.reinsurance.treaties.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.reinsurance.help.page.title'"
        [descriptionKey]="'insurance.reinsurance.help.page.description'"
        [bulletKeys]="[
          'insurance.reinsurance.help.page.bullets.scope',
          'insurance.reinsurance.help.page.bullets.treaties',
          'insurance.reinsurance.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.reinsurance.treaties.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.reinsurance.help.listFilters.title'"
                [descriptionKey]="'insurance.reinsurance.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.reinsurance.help.listFilters.bullets.search',
                  'insurance.reinsurance.help.listFilters.bullets.type',
                  'insurance.reinsurance.help.listFilters.bullets.selection'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.reinsurance.treaties.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.reinsurance.treaties.fields.type') }}</span>
                <p-select
                  [options]="treatyTypeOptions"
                  optionValue="value"
                  [ngModel]="typeFilter()"
                  (ngModelChange)="onTypeFilterChange($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.reinsurance.treaties.fields.status') }}</span>
                <p-select
                  [options]="treatyStatusOptions"
                  optionValue="value"
                  [ngModel]="statusFilter()"
                  (ngModelChange)="onStatusFilterChange($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.reinsurance.treaties.states.empty')" [message]="t('insurance.reinsurance.treaties.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.code') }}</th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.name') }}</th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.type') }}</th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.status') }}</th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.effectiveFrom') }}</th>
                  <th>{{ t('insurance.reinsurance.treaties.fields.effectiveTo') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ t(treatyTypeLabelKey(row.treatyType)) }}</td>
                  <td>{{ t(treatyStatusLabelKey(row.status)) }}</td>
                  <td>{{ row.effectiveFrom | ainDate }}</td>
                  <td>{{ row.effectiveTo | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceReinsuranceTreatiesShellComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceReinsuranceTreatyListItem[]>([]);
  readonly selectedRows = signal<InsuranceReinsuranceTreatyListItem[]>([]);
  readonly search = signal('');
  readonly typeFilter = signal<string | null>(null);
  readonly statusFilter = signal<string | null>(null);
  readonly treatyTypeOptions = [...REINSURANCE_TREATY_TYPE_OPTIONS];
  readonly treatyStatusOptions = [...REINSURANCE_TREATY_STATUS_OPTIONS];
  readonly treatyTypeLabelKey = treatyTypeLabelKey;
  readonly treatyStatusLabelKey = treatyStatusLabelKey;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listReinsuranceTreaties().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.error.set('');
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceReinsuranceTreatyListItem[] {
    const value = this.search().trim().toLowerCase();
    const type = this.typeFilter();
    const status = this.statusFilter();
    return this.rows().filter(row => {
      const matchSearch =
        !value ||
        row.code.toLowerCase().includes(value) ||
        row.name.toLowerCase().includes(value) ||
        row.treatyType.toLowerCase().includes(value);
      const matchType = !type || row.treatyType === type;
      const matchStatus = !status || row.status === status;
      return matchSearch && matchType && matchStatus;
    });
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  onTypeFilterChange(value: string | null): void {
    this.typeFilter.set(value);
  }

  onStatusFilterChange(value: string | null): void {
    this.statusFilter.set(value);
  }

  onSelectionChange(selection: InsuranceReinsuranceTreatyListItem[] | InsuranceReinsuranceTreatyListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/reinsurance/treaties', id]);
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/reinsurance/treaties/new']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
